import type { ReactNode } from 'react'

/**
 * Het wachtteken A2 (besluit van de productowner van 4 november 2026, ontwerp 10.4).
 *
 * Wie wacht moet zien dat er iets gebeurt en dat het niet hangt. Het teken is een gulden rechthoek
 * rechtop, zoals het NWO-logo uit een gulden rechthoek is opgebouwd, zonder het logo na te tekenen: het
 * grote vierkant onder, en de vulling loopt omhoog door de kleinere vierkanten. Elk vlak gaat van licht
 * naar primair en terug; het volgende begint wat later. De tijden en kleuren staan in de tokens
 * (`component.spinner`), de vorm hier: vijf vierkanten die samen een vlak van 50 bij 81 vullen.
 *
 * **Decoratief.** Het teken is `aria-hidden`: de zin ernaast zegt wat er gebeurt, en die draagt de
 * betekenis (R-139). Bij verminderde beweging staat het stil, als gevulde vorm.
 *
 * **Eén teken voor het hele product.** De renderer en de configurator gebruiken dit component; de
 * draaiende ring die er tot 4 november 2026 in beide stond, is weg.
 */
export interface SpinnerProps {
  /** `small` in een knop of label (een letter hoog), `medium` naast een zin, `large` op een wachtscherm. */
  readonly size?: SpinnerSize
  /**
   * Op een donkere of primaire grond: wit op 30 procent naar wit. Op een primaire knop gebeurt dat
   * vanzelf (de stylesheet kent die knoppen); dit is voor een andere donkere plek.
   */
  readonly inverse?: boolean
  readonly className?: string
}

export type SpinnerSize = 'small' | 'medium' | 'large'

/** De vijf vierkanten in de volgorde waarin ze vollopen: links, boven, breedte en hoogte in het vlak van 50 bij 81. */
export const SPINNER_SQUARES: readonly (readonly [left: number, top: number, side: number])[] = [
  [0, 31, 50],
  [0, 0, 31],
  [31, 0, 19],
  [38, 19, 12],
  [31, 24, 7],
]

const SIZE_WORD: Record<SpinnerSize, string> = { small: 'klein', medium: 'middel', large: 'groot' }

/**
 * De maat als attribuut, voor waar de stylesheet (nog) niet is geladen: zonder maat tekent de browser een
 * SVG van 300 bij 150 en duwt hij een knop uit zijn vorm. De stylesheet zet de maat uit de tokens en gaat
 * voor (`component.spinner.height`); dit is alleen de vorm van 50 bij 81 op de maat van de letter.
 */
const FALLBACK: Record<SpinnerSize, { readonly width: string; readonly height: string }> = {
  small: { width: `${(50 / 81).toFixed(3)}em`, height: '1em' },
  medium: { width: `${((2 * 50) / 81).toFixed(3)}em`, height: '2em' },
  large: { width: '50', height: '81' },
}

export function Spinner({ size = 'small', inverse = false, className }: SpinnerProps): ReactNode {
  return (
    <svg
      className={['ef-wachtteken', className].filter((part) => part !== undefined).join(' ')}
      data-maat={SIZE_WORD[size]}
      {...(inverse ? { 'data-grond': 'donker' } : {})}
      viewBox="0 0 50 81"
      width={FALLBACK[size].width}
      height={FALLBACK[size].height}
      aria-hidden="true"
      focusable="false"
    >
      {SPINNER_SQUARES.map(([left, top, side], index) => (
        <rect key={index} className="ef-wachtteken__vlak" x={left} y={top} width={side} height={side} />
      ))}
    </svg>
  )
}
